// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------


// React
import React, {useState, useEffect, useContext} from "react";
import { NavLink } from "react-router-dom";

// Material UI
import { 
    Box, 
    Card, 
    CardMedia,
    CardContent,
    Typography,
    List,
    ListItem,
    Link,
    CircularProgress
} from "@mui/material";

// API
import { fetchVideos } from '../api/course-service';

// Custom Components
import { UserContext } from "../Contexts";
import Error from "./Error";
import LoadingIcon from "./loading/LoadingIcon";
import RenderIf from "./utils/RenderIf";

// Custom Scripts
import { LoadedData, LoadedDataState, useLoadedData } from "../scripts/loadedData/loadedData";
import { constructCloudFrontURL } from "../scripts/utils";

// Constants
import { VIDEO_TYPES } from "../constants";

// ----------------------------------------------------------------------------
//   Card Components
// ----------------------------------------------------------------------------


/**
 * Render a thumbnail card for a single video.
 *
 * @param {Object} props
 * @param {string} props.id    Unique ID of the video
 * @param {string} props.url   S3 path to video file
 * @param {string} props.name  Display name of the video
 */
function VideoCard({ id, url, name, videoType }) {

    // Construct the URL for the thumbnail
    // TODO: Better organization of files
    const parts = url.split('/');
    const thumbURL = `${ parts.slice(0, parts.length - 1).join('/') }/thumb/${ parts.at(-1).replace('.mp4', '') }/action.jpg`;

    // State for image loading error
    const [imageError, setImageError] = useState(false);

    // Component
    return (
        <ListItem
            key={ id }
            sx={{
                width: {
                    xs: 'auto',
                    sm: 500,
                    md: 350,
                },
                minWidth: 350,
                p: 0,
            }}
        >
            <NavLink
                to={videoType === VIDEO_TYPES.PRACTICE_VIDEO ? `/video/${ id }` : encodeURIComponent(id) }
                style={{ textDecoration: 'none', width: '100%' }}
            >
                <Card sx={{
                    width: '100%',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: '0 8px 24px rgba(68, 71, 173, 0.4)',
                    },
                }}>
                    <CardMedia>
                        <Box
                            component={imageError ? "div" : "img"}
                            src={imageError ? undefined : `${constructCloudFrontURL(thumbURL)}`}
                            onError={() => setImageError(true)}
                            sx={{
                                width: '100%',
                                aspectRatio: '16/9',
                                backgroundColor: imageError ? '#000' : 'transparent',
                            }}
                        />
                    </CardMedia>
                    <CardContent sx={{
                        height: {
                            xs: 10,
                            sm: 20,
                            md: 30,
                        },
                    }}>
                        <Typography variant="h6"
                            textAlign  = "center"
                            fontWeight = "bold"
                        >
                            {videoType === VIDEO_TYPES.PRACTICE_VIDEO && 'Video ' }
                            { name.toUpperCase() }
                        </Typography>
                    </CardContent>
                </Card>
            </NavLink>
        </ListItem>
    )
}


/**
 * Render thumbnail cards for a list of videos.
 */
export function VideoCardList({ videos, videoType }) {
    return (
        <List sx={{
            display: 'flex',
            flexDirection: {
                xs: 'column',
                sm: 'column',
                md: 'row',
            },
            flexWrap: {
                xs: 'nowrap',
                sm: 'nowrap',
                md: 'wrap',
            },
            gap: '48px',
            justifyContent: 'center',
        }}>
            {
                videos.map( video => (
                    <VideoCard key={ video.id } { ...video } videoType={ videoType } />
                ))
            }
        </List>
    )
}


// ----------------------------------------------------------------------------
//   Main Component
// ----------------------------------------------------------------------------


const StudentDashboard = () => {

    // Context
    const { userData } = useContext( UserContext )
    const allowAdminFeatures = userData?.value?.role === 'admin';

    // State
    const [ videos, videoManager ] = useLoadedData()

    // Load videos from API
    useEffect(() => {
        videoManager.setWorking();
        fetchVideos().then(
            data  => { videoManager.setSuccess( data  ); },
            error => { videoManager.setFailure( error ); }
        );
    }, []);

    /** The list of all videos returned from the API (typesafe) */
    const videosListAll = videos?.value || [];

    /** The list of visible / public videos only */
    const videosListVisible = videosListAll.filter(({ isPublic = false }) => isPublic);


    // Special Cases

    if ( videos.state === LoadedDataState.FAILURE ) {
        return <Error message={ videos.error?.message } />
    }

    // Main Layout

    return (
        <Box height="100%">

            {/* Current Videos List */}
            <Typography variant="h5" sx={{marginTop: '2rem'}}>Assigned Videos:</Typography>
            <RenderIf
                condition = { ![LoadedDataState.WORKING, LoadedDataState.NONE].includes( videos.state ) }
                fallback  = { <LoadingIcon /> }
            >
                <RenderIf condition={ videosListVisible.length } fallback={ <p>No videos found</p> } >
                    <VideoCardList videos={ videosListVisible } videoType={VIDEO_TYPES.PRACTICE_VIDEO} />
                </RenderIf>
            </RenderIf>

            {/* All Videos (Admins Only) */}
            <RenderIf condition={ allowAdminFeatures }>
                <Typography variant="h5" sx={{marginTop: '2rem'}}>
                    All Videos
                </Typography>
                <RenderIf condition={ videosListAll.length } fallback={ <p>No videos found</p> } >
                    <VideoCardList videos={ videosListAll } videoType={VIDEO_TYPES.PRACTICE_VIDEO} />
                </RenderIf>
            </RenderIf>

        </Box>
    );
}



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------

export default StudentDashboard;
